"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AnnotationCanvas = void 0;
var tslib_1 = require("tslib");
var react_1 = tslib_1.__importStar(require("react"));
var rxjs_1 = require("rxjs");
var styled_components_1 = tslib_1.__importDefault(require("styled-components"));
var utils_1 = require("../utils");
var annotation_canvas_view_1 = require("./annotation-canvas-view");
var default_label_1 = require("./default-values/default-label");
var fit_to_grid_fns_1 = require("./functions/fit-to-grid-fns");
var use_annotation_canvas_dataflow_1 = require("./hooks/dataflow/use-annotation-canvas-dataflow");
var use_mouse_event_1 = require("./hooks/use-mouse-event");
var id_type_1 = require("./types/id-type");
var annotation_canvas_style_1 = require("./types/record/annotation-canvas-style");
var Root = styled_components_1.default.div(templateObject_1 || (templateObject_1 = tslib_1.__makeTemplateObject(["\n  width: 100%;\n  height: 100%;\n  -webkit-touch-callout: none;\n  user-select: none;\n"], ["\n  width: 100%;\n  height: 100%;\n  -webkit-touch-callout: none;\n  user-select: none;\n"])));
/**
 * - image
 *   @param children 画像・動画等の要素
 *   @param imagePixelSize 画像の縦横ピクセル数
 * - configs
 *   @param styles CSSスタイルのカスタマイズ項目
 *   @param gridSize canvas object をスナップするグリッドの本数（縦・横）
 *   @param idMaker canvas object 新規作成時に使用するid生成関数（デフォルトは uuid v4）
 *   @param labels ラベル文字列リスト（省略した場合は`['']`）
 *   @param labelColorHues ラベルごとの色相の整数値 (0-359)
 * - canvas objects
 *   @param bboxesRelative bounding boxのリスト（画像を1x1としたときの相対サイズ）
 *   @param onBboxesChange bounding boxリスト変更時のコールバック
 *   @param paintObjects paintオブジェクトのリスト（画像のピクセルに対応するラスターデータ）
 *   @param selectedId 選択されているcanvas objectのid
 *   @param onSelectedIdChange `selectedId`変更時のコールバック
 *   @param highlightId ハイライト表示するcanvas objectのid
 *   @param onHighlightIdChange `highlightId`変更時のコールバック
 *   @param visibleLabels labelsのうちキャンバス上に該当するbboxを表示するもののリスト
 *   @param selectedLabel 現在選択中のラベル．新規作成時にそのラベルが付けられる．
 * - mode
 *   @param zoomable ズームできるかどうか on/off (boolean)
 *   @param moveImageMode 画像移動モードのon/off (boolean)
 *   @param annotationMode モード (`'readonly' | 'move-image' | 'rectbox' | 'auto-segmentation'`)
 *   @param onInternalModeChange エディタの内部モード変更時のコールバック
 *   @param onCrop `auto-segmentation`モードで矩形範囲選択時のコールバック（引数には画像を1x1としたときの相対サイズを渡す）
 * - external action
 *   @param action$ 外部から受け取るアクション (`'undo' | 'redo' | 'deselect-object' | 'remove-selected' | 'remove-all' | 'zoom-up' | 'zoom-out' | 'reset-zoom' | 'reset-pointer-pos'`)
 */
var noop = function () { return undefined; };
exports.AnnotationCanvas = (0, react_1.memo)(function (_a) {
    /* from props */
    var children = _a.children, _b = _a.imagePixelSize, imagePixelSizeInput = _b === void 0 ? (0, utils_1.IRectSize)() : _b, 
    /* configs */
    stylesInput = _a.styles, _c = _a.gridSize, gridSize = _c === void 0 ? 1 << 10 : _c, _d = _a.idMaker, idMaker = _d === void 0 ? id_type_1.defaultIdMaker : _d, 
    /* canvas objects */
    _e = _a.bboxesRelative, 
    /* canvas objects */
    bboxesRelativeInput = _e === void 0 ? (0, utils_1.IList)() : _e, _f = _a.onBboxesRelativeChange, onBboxesRelativeChange = _f === void 0 ? noop : _f, _g = _a.paintObjects, paintObjectsInput = _g === void 0 ? (0, utils_1.IList)() : _g, selectedIdInput = _a.selectedId, _h = _a.onSelectedIdChange, onSelectedIdChange = _h === void 0 ? noop : _h, highlightIdInput = _a.highlightId, _j = _a.onHighlightIdChange, onHighlightIdChange = _j === void 0 ? noop : _j, _k = _a.visibleLabels, visibleLabelsInput = _k === void 0 ? (0, utils_1.IList)([default_label_1.defaultLabel]) : _k, selectedLabelInput = _a.selectedLabel, 
    /* mode */
    _l = _a.zoomable, 
    /* mode */
    zoomableInput = _l === void 0 ? true : _l, _m = _a.moveImageMode, moveImageMode = _m === void 0 ? false : _m, _o = _a.annotationMode, annotationMode = _o === void 0 ? 'readonly' : _o, _p = _a.onInternalModeChange, onInternalModeChange = _p === void 0 ? noop : _p, _q = _a.onCropRectRelativeChange, onCrop = _q === void 0 ? noop : _q, 
    /* external action */
    _r = _a.action$, 
    /* external action */
    action$ = _r === void 0 ? rxjs_1.NEVER : _r;
    // required
    var imagePixelSize$ = (0, utils_1.useValueAsStream)(imagePixelSizeInput);
    // configs (set only once when creating a component)
    var styles = (0, react_1.useMemo)(function () { return (0, annotation_canvas_style_1.IAnnotationCanvasStyle)(stylesInput); }, [] // eslint-disable-line react-hooks/exhaustive-deps
    );
    var _s = (0, react_1.useMemo)(function () { return (0, fit_to_grid_fns_1.fitToGridFunctions)(gridSize); }, [] // eslint-disable-line react-hooks/exhaustive-deps
    ), fitPosToGrid = _s.fitPosToGrid, fitRectSizeToGrid = _s.fitRectSizeToGrid, fitRectToGrid = _s.fitRectToGrid;
    // canvas objects
    var bboxesRelativeInputSnapped = (0, react_1.useMemo)(function () { return bboxesRelativeInput.map(function (bbox) { return bbox.update('rect', fitRectToGrid); }); }, [bboxesRelativeInput, fitRectToGrid]);
    var bboxesRelativeInput$ = (0, utils_1.useValueAsStream)(bboxesRelativeInputSnapped);
    var paintObjectsInput$ = (0, utils_1.useValueAsStream)(paintObjectsInput);
    var selectedId$ = (0, utils_1.useValueAsStream)(selectedIdInput);
    var highlightId$ = (0, utils_1.useValueAsStream)(highlightIdInput);
    var visibleLabels$ = (0, utils_1.useValueAsStream)(visibleLabelsInput);
    var selectedLabel$ = (0, utils_1.useValueAsStream)(selectedLabelInput);
    var zoomable$ = (0, utils_1.useValueAsStream)(zoomableInput);
    var moveImageMode$ = (0, utils_1.useValueAsStream)(moveImageMode);
    var annotationMode$ = (0, utils_1.useValueAsStream)(annotationMode);
    /* events */
    var _t = tslib_1.__read((0, utils_1.useResizeObserverAsStream)(), 2), rootRectSize$ = _t[0], targetElRef = _t[1];
    var _u = (0, use_mouse_event_1.useMouseEvent)(), mouseEvent$ = _u.mouseEvent$, onMouseLeftDown = _u.onMouseLeftDown, onMouseLeftUp = _u.onMouseLeftUp, onMouseRightDown = _u.onMouseRightDown, onMouseRightUp = _u.onMouseRightUp, onMouseLeave = _u.onMouseLeave, onMouseEnter = _u.onMouseEnter;
    var _v = tslib_1.__read((0, utils_1.useStateAsStream)(undefined), 2), 
    // dummy comment to control prettier
    mousePos$ = _v[0], onMouseMove = _v[1];
    var _w = tslib_1.__read((0, utils_1.useStateAsStream)(0), 2), wheelScrollDelta$ = _w[0], onWheelScroll = _w[1];
    /* extract values */
    var _x = (0, use_annotation_canvas_dataflow_1.useAnnotationCanvasDataflow)(fitPosToGrid, fitRectSizeToGrid, idMaker, styles, 
    /* props */
    imagePixelSize$, zoomable$, moveImageMode$, annotationMode$, bboxesRelativeInput$, paintObjectsInput$, selectedId$, highlightId$, visibleLabels$, selectedLabel$, action$, 
    /* events */
    mouseEvent$, mousePos$, wheelScrollDelta$, rootRectSize$, 
    /* callbacks */
    onBboxesRelativeChange, onSelectedIdChange, onHighlightIdChange, onInternalModeChange, onCrop), pointerPosAbsolute = _x.pointerPosAbsolute, canvasRectSize = _x.canvasRectSize, virtualImgRectAbsolute = _x.virtualImgRectAbsolute, imgVisibleAreaRectAbsolute = _x.imgVisibleAreaRectAbsolute, cropTemporaryRectAbsolute = _x.cropTemporaryRectAbsolute, addBboxTemporaryRectAbsolute = _x.addBboxTemporaryRectAbsolute, resizeBboxTemporaryRectAbsolute = _x.resizeBboxTemporaryRectAbsolute, bboxesAbsolute = _x.bboxesAbsolute, selectedId = _x.selectedId, highlightId = _x.highlightId, cursorType = _x.cursorType, mode = _x.mode;
    var mouseEventCallbacks = (0, react_1.useMemo)(function () { return ({
        onMouseLeftDown: onMouseLeftDown,
        onMouseLeftUp: onMouseLeftUp,
        onMouseRightDown: onMouseRightDown,
        onMouseRightUp: onMouseRightUp,
        onMouseLeave: onMouseLeave,
        onMouseEnter: onMouseEnter,
        onMouseMove: onMouseMove
    }); }, [
        onMouseLeftDown,
        onMouseLeftUp,
        onMouseRightDown,
        onMouseRightUp,
        onMouseLeave,
        onMouseEnter,
        onMouseMove
    ]);
    // TODO
    // const touchEvents = useMemo(
    //   () => ({
    //     onTouchStart: () => {},
    //     onTouchEnd: () => {},
    //     onTouchMove: () => {}
    //   }),
    //   []
    // )
    var imagePixelSize = (0, utils_1.useStreamValue)(imagePixelSize$, (0, utils_1.IRectSize)());
    return (react_1.default.createElement(Root, { ref: targetElRef },
        react_1.default.createElement(annotation_canvas_view_1.AnnotationCanvasView, { styles: styles, imagePixelSize: imagePixelSize, canvasRectSize: canvasRectSize, imgVisibleAreaRectAbsolute: imgVisibleAreaRectAbsolute, mouseEventCallbacks: mouseEventCallbacks, 
            // touchEvents={touchEvents}
            onWheelScroll: onWheelScroll, pointerPosOnImg: pointerPosAbsolute, cropTemporaryRectOnImg: cropTemporaryRectAbsolute, addBboxTemporaryRectOnImg: addBboxTemporaryRectAbsolute, resizeBboxTemporaryRectOnImg: resizeBboxTemporaryRectAbsolute, bboxesOnImg: bboxesAbsolute, paintObjects: paintObjectsInput, selectedId: selectedId, highlightId: highlightId, virtualImgRectAbsolute: virtualImgRectAbsolute, cursorType: cursorType, mode: mode }, children)));
});
exports.AnnotationCanvas.displayName = 'AnnotationCanvas';
var templateObject_1;
